import { DurationType } from "../types";

const moneyFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

/** 5000 -> "SAR 5,000.00" */
export const formatMoney = (value: string | number | null | undefined, currency = "SAR"): string => {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    if (Number.isNaN(n)) return "—";
    return `${currency} ${moneyFormatter.format(n)}`;
};

/** Compact for stat cards: 125000 -> "125K" */
export const formatCompact = (value: number): string =>
    new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO -> "24-10-2026" (dates are stored as UTC midnight) */
export const formatDate = (value: string | null | undefined): string => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()}`;
};

/** ISO -> "2026-10-24" for <input type="date"> */
export const toInputDate = (value: string | null | undefined): string => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** Today as "YYYY-MM-DD" in local time */
export const todayInput = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Whole days from today to the date (negative = past). */
export const daysUntil = (value: string | null | undefined): number | null => {
    if (!value) return null;
    const target = new Date(toInputDate(value) + "T00:00:00Z").getTime();
    const today = new Date(todayInput() + "T00:00:00Z").getTime();
    return Math.round((target - today) / 86_400_000);
};

/** Same rule as the backend: months/years clamp to month end (31 Jan + 1 month = 28 Feb). */
export const addPlanDuration = (startInput: string, duration: number, type: DurationType): string => {
    if (!startInput) return "";
    const d = new Date(startInput + "T00:00:00Z");

    if (type === "DAY") d.setUTCDate(d.getUTCDate() + duration);
    else if (type === "WEEK") d.setUTCDate(d.getUTCDate() + duration * 7);
    else {
        const months = type === "MONTH" ? duration : duration * 12;
        const day = d.getUTCDate();
        d.setUTCDate(1);
        d.setUTCMonth(d.getUTCMonth() + months);
        const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
        d.setUTCDate(Math.min(day, lastDay));
    }

    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** 1 YEAR -> "1 year", 6 MONTH -> "6 months" */
export const formatDuration = (duration: number, type: DurationType): string => {
    const unit = type.toLowerCase();
    return `${duration} ${unit}${duration === 1 ? "" : "s"}`;
};

/** "PARTIALLY_PAID" -> "Partially paid" */
export const humanize = (value: string | null | undefined): string => {
    if (!value) return "—";
    const s = value.replace(/_/g, " ").toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export const initials = (name: string | undefined): string =>
    (name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]!.toUpperCase())
        .join("");